import { useEffect, useState } from "react";
import { Button, Modal } from "semantic-ui-react";
import { ApplicationPermission } from "../models/ApplicationPermission";
import { FormError } from "../models/FormError";
import { AdminRole } from "../models/AdminRole";
import EditAdminRoleForm, { FormData } from "./EditAdminRoleForm";

interface Props {
  open: boolean;
  onClose: () => void;
  onSubmit: (displayName: string, permissionNames: string[], roleId?: string) => void;
  submitInProgress: boolean;
  submitErrors?: string[];
  adminRole?: AdminRole;
  adminPermissions?: ApplicationPermission[];
}

const EditAdminRoleModal = ({
  open,
  onClose,
  onSubmit,
  submitInProgress,
  submitErrors,
  adminRole,
  adminPermissions,
}: Props) => {
  const [formData, setFormData] = useState<FormData>({} as FormData);
  const [formErrors, setFormErrors] = useState<FormError>({});
  const [hasSubmitted, setHasSubmitted] = useState(false);

  useEffect(() => {
    if (open) {
      setFormData({
        ...adminRole,
        displayName: adminRole?.name.replace(/^[^_]*_/, ""),
        permissionNames: adminRole?.permissions?.map((p) => p?.name),
      } as FormData);
    } else {
      setFormData({} as FormData);
      setFormErrors({} as FormError);
      setHasSubmitted(false);
    }
  }, [open, adminRole]);

  const validateFormData = (formData: FormData) => {
    const formError: FormError = {};

    if (!formData.displayName) {
      formError.displayName = "Please enter role name";
    }

    return formError;
  };

  const handleSubmit = () => {
    setHasSubmitted(true);

    const validationErrors = validateFormData(formData);
    setFormErrors(validationErrors);

    // If there is no validation error
    if (Object.keys(validationErrors).length === 0) {
      const { displayName, permissionNames } = formData;
      onSubmit(displayName, permissionNames, adminRole?.id);
    }
  };

  const handleFormChange = (formData: FormData) => {
    if (hasSubmitted) {
      const validationErrors = validateFormData(formData);
      setFormErrors(validationErrors);
    }

    setFormData({ ...formData });
  };

  const handelClose = () => {
    onClose();
  };

  return (
    <Modal open={open}>
      <Modal.Header>Role details</Modal.Header>
      <Modal.Content>
        <Modal.Description>
          <EditAdminRoleForm
            formErrors={formErrors}
            errors={submitErrors}
            formData={formData}
            onChange={handleFormChange}
            adminPermissions={adminPermissions}
          ></EditAdminRoleForm>
        </Modal.Description>
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={handelClose}>Cancel</Button>
        <Button onClick={handleSubmit} positive loading={submitInProgress} disabled={submitInProgress}>
          Ok
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

export default EditAdminRoleModal;
